import { FC, useMemo, useState } from "react";
import { useForm } from "@mantine/form";
import {
  Button,
  FormSection,
  Grid,
  SelectBox,
  TextAreaInput,
  TextInput,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import validateAsync from "@/utils/ZodHelpers/validateAsync";
import resolveCustomFieldValidations from "@/components/Forms/utils/resolveCustomFieldValidations";
import CustomFieldRender from "@/components/Forms/components/CustomFieldRender";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import { ClientSelector } from "@/Features/Clients/components";
import CaseTypesApi from "@/api/case-types";
import Loader from "@/components/Loader";
import { InquiryStatusOptions } from "@/pages/Inquiries/InquiryDetails";

import { useCustomFields, CustomField } from "../hooks/useCustomFields";
import { transformCustomAttributes } from "./utils/customAttributeUtils";
import type {
  EditInquiryFormData,
  Inquiry,
  InquiryFormData,
} from "../inquiries.types";

export type InquiryFormMode = "create" | "edit";

interface DefaultInquiryFormProps {
  mode: InquiryFormMode;
  defaultFormData?: Partial<Inquiry>;
  buttonProps?: { cancelText?: string; submitText?: string };
  onSubmit: (
    data: InquiryFormData,
    diffData?: EditInquiryFormData
  ) => void | Promise<void>;
  onCancel: () => void;
}

type CaseType = { case_type: string };

const buildSchema = (
  mode: InquiryFormMode,
  customFields: CustomField[] = []
) => {
  const base: Record<string, z.ZodTypeAny> = {
    request_name: z.string().nullable().optional(),
    inquiry_type: z
      .string({ required_error: "Inquiry type is required" })
      .min(1, { message: "Inquiry type is required" }),
    inquiry_referred: z.string().nullable().optional(),
    inquiry_notes: z
      .string({ required_error: "Description is required" })
      .min(1, { message: "Description is required" }),
  };

  if (mode === "create") {
    base.linked_client = z
      .any()
      .refine((v) => v != null && (v as { value?: unknown }).value != null, {
        message: "Client is required",
      });
  } else {
    base.inquiry_status = z
      .string({ required_error: "Status is required" })
      .min(1, { message: "Status is required" });
  }

  return z.object({
    ...base,
    ...resolveCustomFieldValidations(customFields),
  });
};

const buildInitialValues = (
  mode: InquiryFormMode,
  defaultFormData?: Partial<Inquiry>
): InquiryFormData => {
  const customAttributeValues = transformCustomAttributes(
    defaultFormData || {}
  );

  if (mode === "edit") {
    return {
      request_name: defaultFormData?.request_name ?? "",
      inquiry_type: defaultFormData?.inquiry_type ?? "",
      inquiry_status: defaultFormData?.inquiry_status ?? "",
      inquiry_referred: defaultFormData?.inquiry_referred ?? "",
      inquiry_notes: defaultFormData?.inquiry_notes ?? "",
      ...customAttributeValues,
    };
  }

  return {
    request_name: "",
    linked_client: null,
    client_id: null,
    inquiry_type: "",
    inquiry_referred: "",
    inquiry_notes: "",
    ...customAttributeValues,
  };
};

const DefaultInquiryForm: FC<DefaultInquiryFormProps> = ({
  mode,
  defaultFormData,
  buttonProps,
  onSubmit,
  onCancel,
}) => {
  const theme = useTheme();
  const [submitting, setSubmitting] = useState(false);
  const { data: customFields = [], isFetched: customFieldsFetched } =
    useCustomFields();

  const { data: caseTypes = [] } = useQuery<CaseType[]>({
    queryKey: ["cases:types"],
    queryFn: () => CaseTypesApi.getCaseTypes(),
  });

  const inquiryTypeOptions = useMemo(
    () =>
      caseTypes.map((ct) => ({
        label: ct.case_type,
        value: ct.case_type,
      })),
    [caseTypes]
  );

  const statusOptions = useMemo(
    () =>
      InquiryStatusOptions.map((status) => ({
        label: status,
        value: status,
      })),
    []
  );

  const initialValues = useMemo(
    () => buildInitialValues(mode, defaultFormData),
    [mode, defaultFormData]
  );

  const customFieldsHaveData = useMemo(
    () =>
      Object.entries(initialValues).some(
        ([key, val]) =>
          key.startsWith("custom_attribute_") &&
          val !== null &&
          val !== undefined &&
          val !== ""
      ),
    [initialValues]
  );

  const hasRequiredCustomField = useMemo(
    () => customFields.some((f: CustomField) => f.is_required === 1),
    [customFields]
  );

  const form = useForm<InquiryFormData>({
    mode: "controlled",
    initialValues,
  });

  const handleSubmit = async () => {
    if (submitting) return;

    const formData = form.getValues();
    const dirty = form.getDirty() as Record<string, boolean>;

    const cfForValidation =
      mode === "edit"
        ? (customFields as CustomField[]).filter(
            (f) => dirty[`custom_attribute_${f.field_id}`]
          )
        : (customFields as CustomField[]);

    const schema = buildSchema(mode, cfForValidation);
    const result = await validateAsync(schema)(formData);
    if (result?.hasErrors) {
      console.error("Validation Errors", result?.errors);
      form.setErrors(result?.errors);
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "edit") {
        const diff: EditInquiryFormData = {};
        Object.keys(dirty).forEach((key) => {
          if (dirty[key]) {
            (diff as Record<string, unknown>)[key] = (
              formData as Record<string, unknown>
            )[key];
          }
        });
        await onSubmit(formData, diff);
        return;
      }

      await onSubmit(formData);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  if (!customFieldsFetched) {
    return <Loader message="Loading inquiry form..." />;
  }

  return (
    <>
      <Grid col={2}>
        <TextInput
          size="sm"
          variant="outlined"
          label="Request Name"
          placeholder="Enter a name for this inquiry"
          description="A short label for this inquiry"
          key={form.key("request_name")}
          {...form.getInputProps("request_name")}
        />
        {mode === "create" ? (
          <ClientSelector
            size="sm"
            variant="outlined"
            label="Client"
            placeholder="Select or search for a client..."
            description="Select an existing client or create a new one"
            required
            clearable
            allowCreateNew
            onClientCreated={(client) => {
              form.setFieldValue("client_id", client.client_id);
              form.setFieldValue("linked_client", {
                value: client.client_id,
                label: client.name,
                data: client,
              });
            }}
            key={form.key("linked_client")}
            {...form.getInputProps("linked_client")}
            onChange={(_, option) => {
              form.setFieldValue("linked_client", option ?? null);
              form.setFieldValue(
                "client_id",
                (option?.value as number | undefined) ?? null
              );
            }}
          />
        ) : (
          <SelectBox
            size="sm"
            variant="outlined"
            label="Status"
            placeholder="Select inquiry status"
            description="The current status of this inquiry"
            required
            clearable={false}
            searchable
            data={statusOptions}
            key={form.key("inquiry_status")}
            {...form.getInputProps("inquiry_status")}
          />
        )}
        <SelectBox
          size="sm"
          variant="outlined"
          label="Inquiry Type"
          placeholder="Select inquiry type"
          description="The case type for this inquiry"
          required
          clearable={false}
          searchable
          data={inquiryTypeOptions}
          key={form.key("inquiry_type")}
          {...form.getInputProps("inquiry_type")}
        />
        <TextInput
          size="sm"
          variant="outlined"
          label="Referred By"
          placeholder="Who referred this client?"
          description="Where the inquiry was referred from"
          key={form.key("inquiry_referred")}
          {...form.getInputProps("inquiry_referred")}
        />
      </Grid>
      <div style={{ marginTop: "1rem" }}>
        <TextAreaInput
          size="sm"
          variant="outlined"
          label="Description"
          placeholder="Describe the inquiry"
          description="Details about the inquiry"
          required
          key={form.key("inquiry_notes")}
          {...form.getInputProps("inquiry_notes")}
        />
      </div>

      <FormSection
        title="Custom Fields"
        defaultOpen={hasRequiredCustomField || customFieldsHaveData}
        style={{ marginTop: 10 }}
      >
        {customFields && customFields.length > 0 ? (
          <Grid col={2}>
            {customFields.map((field: CustomField) => (
              <CustomFieldRender
                key={form.key(`custom_attribute_${field.field_id}`)}
                field={field}
                inputProps={form.getInputProps(
                  `custom_attribute_${field.field_id}`
                )}
                onChange={() => {}}
              />
            ))}
          </Grid>
        ) : (
          <div>
            No Inquiry Custom Fields configured.{" "}
            <Link
              to="/settings/custom-fields"
              style={{
                textDecoration: "underline",
                color: theme.palette.primary.main,
              }}
            >
              Add custom fields here
            </Link>
          </div>
        )}
      </FormSection>

      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          {buttonProps?.cancelText || "Cancel"}
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          {buttonProps?.submitText || "Submit"}
        </Button>
      </ButtonMenu>
    </>
  );
};

export default DefaultInquiryForm;
